import { useEffect, useState } from 'react';
import { checkForNewVersion, isNewVersionReady, onNewVersionReady } from '../utils/newVersion';

/** Whether a new version of the app is live (utils/newVersion.ts): true once, until a reload. */
export const useNewVersionReady = (): boolean => {
  const [ready, setReady] = useState(isNewVersionReady);
  useEffect(() => {
    if (isNewVersionReady()) setReady(true);
    return onNewVersionReady(() => setReady(true));
  }, []);
  return ready;
};

const EVERY_MS = 10 * 60 * 1000;
const NOT_MORE_OFTEN_MS = 60 * 1000;

/**
 * Looks for a newer deployed version so the person can reload BEFORE anything breaks: when signed
 * in (a sign-in does not reload the page), when the tab comes back into view, and every 10 minutes
 * while it is in view. A 1.5 KB request for the app's own page; never more than once a minute.
 */
export const useNewVersionCheck = (enabled: boolean): void => {
  useEffect(() => {
    if (!enabled || process.env.NODE_ENV !== 'production') return undefined;
    let lastAt = 0;
    const check = () => {
      if (isNewVersionReady() || document.visibilityState !== 'visible') return;
      if (Date.now() - lastAt < NOT_MORE_OFTEN_MS) return;
      lastAt = Date.now();
      void checkForNewVersion();
    };
    check();
    const onVisible = () => { if (document.visibilityState === 'visible') check(); };
    document.addEventListener('visibilitychange', onVisible);
    const timer = window.setInterval(check, EVERY_MS);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.clearInterval(timer);
    };
  }, [enabled]);
};
